import type { TelemetryClient } from '@klareospace/pharos-lib-front-utils';
import * as React from 'react';
import { useEffect } from 'react';

import { DefaultErrorPage } from '../components/composed/default-error-page';
import type { DefaultErrorPageProps } from '../components/composed/default-error-page';

/** Component type compatible with TanStack Router's `defaultErrorComponent` prop. */
type TelemetryErrorPage = (props: DefaultErrorPageProps) => React.JSX.Element;

/**
 * Creates a default error page component that tracks the caught error via the
 * telemetry client on mount.
 *
 * Use as `defaultErrorComponent` in `createRouter()` so that route-level errors
 * are automatically forwarded to App Insights (or whichever telemetry backend
 * the client targets).
 *
 * Call once at module level so the returned component reference is stable:
 *
 * ```ts
 * const AppDefaultErrorPage = createTelemetryDefaultErrorPage(telemetryClient, env.isDev);
 * const router = createRouter({ defaultErrorComponent: AppDefaultErrorPage, ... });
 * ```
 *
 * @param telemetryClient - The telemetry client to track exceptions.
 * @param isDev - When `true`, the error message is shown in the fallback UI.
 */
export function createTelemetryDefaultErrorPage(telemetryClient: TelemetryClient, isDev: boolean): TelemetryErrorPage {
  return function TelemetryDefaultErrorPage({ error }: DefaultErrorPageProps): React.JSX.Element {
    useEffect((): void => {
      telemetryClient.trackException(error);
    }, [error]);
    return <DefaultErrorPage error={error} isDev={isDev} />;
  };
}
